<template>
    <div class="metric-filter-list mx-2">
        <div class="row">
            <div class="col">
                <div class="card mb-3 mt-3">
                    <div class="card-body">
                        <h1>Metric, Dimension & Filter Management</h1>
                        <p>Create or edit the metrics, dimensions and filters that power the charts utilized throughout Avid. You can create new values that will be available for all clients or you can select a client from the list below to create some specific to them.</p>
                        <div class="row mb-3">
                            <div class="col-6">
                                <label class="form-label">Select Visibility</label>
                                <accordion-select 
                                    v-model="local_client_selected" 
                                    :options="formatted_clients" 
                                    @update:modelValue="changeClient"
                                />
                            </div>
                            <div class="col-6">
                                <label class="form-label">Data Source</label>
                                <select 
                                    class="form-select" 
                                    name="data_source" 
                                    v-model="data_blend"
                                    @change="changeDataSource"
                                 aria-label="Data Source">
                                    <option 
                                        v-for="d in local_data_blends" 
                                        :key="d.type" 
                                        :value="d"
                                    >
                                        {{ propercase(d.type) }} Data Blend
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    placeholder="Search by Name..."
                                    name="metric_filter_search" 
                                    v-model="search_key" 
                                    @input="searchByName"
                                 aria-label="Search by Name...">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12" v-for="type in data_types" :key="type">
                <div class="card mb-3 table-card">
                    <div class="card-header">
                        <div class="row">
                            <div class="col-lg-6 col-12">
                                <h3 class="mt-2">{{ propercase(type) }}s</h3>
                            </div>
                            <div class="col-lg-6 d-none d-lg-block">
                                <div 
                                    class="d-flex justify-content-end justify-self-end gap-2" 
                                    v-if="getDataTypeOptions(type).length > 0"
                                >
                                    <a 
                                        class="btn btn-secondary mb-2 float-end btn-sm me-2"
                                        @click="getUsage" 
                                        v-if="!get_usage"
                                    >
                                        <i class="fa fas fa-fw fa-eye"></i> Show Usage
                                    </a>
                                    <a 
                                        class="btn btn-primary mb-2 float-end btn-sm"
                                        @click="addMetricFilter(type)" 
                                        data-bs-toggle="modal"
                                        data-bs-target="#metric-filter-modal"
                                    >
                                        <i class="fa fas fa-fw fa-plus"></i> Add a {{ propercase(type) }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card-body p-0">
                        <table class="table table-without-pagination mb-0" v-if="getDataTypeOptions(type).length > 0">
                            <thead>
                                <tr class="table-header-row">
                                    <th class="d-table-cell">Name</th>
                                    <th class="d-none d-sm-table-cell">Source</th>
                                    <th class="d-none d-sm-table-cell" v-if="type === 'metric'">Format</th>
                                    <th class="d-none d-sm-table-cell">Pages in Use</th>
                                    <th class="d-none d-sm-table-cell">Charts in Use</th>
                                    <th class="d-none d-sm-table-cell column-sort-disabled pe-3" width="1">Actions</th>
                                </tr>
                            </thead>
                            <transition-group tag="tbody">
                                <tr v-for="metric in getDataTypeOptions(type)" :key="metric.id">
                                    <td class="d-table-cell">
                                        {{ metric.admin_name }} 
                                        <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" :title="metric.description"
                                            v-if="metric.description != null && metric.description !== ''" alt=""
                                        ></img>
                                    </td>
                                    <td class="d-none d-sm-table-cell">
                                        {{ propercase(metric.data_blend_type) }} Blend
                                    </td>
                                    <td class="d-none d-sm-table-cell" v-if="type === 'metric'">
                                        {{ propercase(metric.format) }}
                                    </td>
                                    <td class="d-none d-sm-table-cell">
                                        <span v-if="!get_usage"> -- </span>
                                        <a 
                                            v-else-if="metric.page_usage_count > 0"
                                            :href="'/admin/report_builder/filter/' + metric.id"
                                        >
                                            {{ number_with_zero(metric.page_usage_count) }}
                                        </a>
                                        <span v-else-if="metric.page_usage_count === 0">
                                            {{ number_with_zero(metric.page_usage_count) }}
                                        </span>
                                        <span v-else class="spinner-border spinner-border-sm text-primary" role="status" />
                                    </td>
                                    <td class="d-none d-sm-table-cell">
                                        <span v-if="!get_usage"> -- </span>
                                        <a 
                                            v-else-if="metric.chart_usage_count > 0"
                                            :href="'/admin/charts/' + metric.id"
                                        >
                                            {{ number_with_zero(metric.chart_usage_count) }}
                                        </a>
                                        <span v-else-if="metric.chart_usage_count === 0">
                                            {{ number_with_zero(metric.chart_usage_count) }}
                                        </span>
                                        <span v-else class="spinner-border spinner-border-sm text-primary" role="status" />
                                    </td>
                                    <td class="d-table-cell">
                                        <div class="dropdown d-flex justify-content-center">
                                            <button 
                                                class="btn btn-white btn-icon-only-md" 
                                                data-bs-toggle="dropdown" 
                                                data-bs-auto-close="true" 
                                                aria-expanded="false"
                                            >
                                                <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" alt="">
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                                <a 
                                                    class="dropdown-item" 
                                                    href="#" 
                                                    @click.prevent="copyMetricFilter(metric)" 
                                                    data-bs-toggle="modal" 
                                                    data-bs-target="#metric-filter-modal"
                                                >
                                                    <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy {{ propercase(type) }}
                                                </a>
                                                <a 
                                                    class="dropdown-item" 
                                                    href="#" 
                                                    @click.prevent="editMetricFilter(metric)" 
                                                    data-bs-toggle="modal" 
                                                    data-bs-target="#metric-filter-modal"
                                                >
                                                    <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit {{ propercase(type) }}
                                                </a>
                                                <hr class="my-1">
                                                <a 
                                                    class="dropdown-item text-danger" 
                                                    href="#" 
                                                    @click.prevent="deleteMetricFilter(metric)"
                                                >
                                                    <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete {{ propercase(type) }}
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </transition-group>
                        </table>
                        <div v-else-if="search_key !== ''" class="text-center px-3 py-3">
                            <p>No {{ propercase(type) }}s match your search criteria</p>
                        </div>
                        <div v-else class="text-center px-3 py-3">
                            <p>No {{ propercase(type) }}s have been created yet. Add your first {{ propercase(type) }} using the button below.</p>
                            <div class="d-flex justify-content-center">
                                <a 
                                    class="btn btn-primary" 
                                    @click="addMetricFilter(type)"
                                    data-bs-toggle="modal" 
                                    data-bs-target="#metric-filter-modal"
                                >
                                    <i class="fas fa-fw fa-plus"></i> Add a {{ propercase(type) }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div 
            class="modal fade" 
            id="metric-filter-modal" 
            data-bs-backdrop="static"
            aria-labelledby="add-metric-filter-modal" 
            aria-hidden="true"
        >
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <div class="text-center w-100">
                            <button type="button" class="btn btn-white float-end close-modal" @click="closeModal" data-bs-toggle="tooltip" title="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                            <h3 class="mt-2">{{ propercase(local_type) }} Editor</h3>
                        </div>
                    </div>
                    <div class="modal-body pt-0 pb-0">
                        <metric-filter-builder 
                            :data_blend="data_blend" 
                            :clients="clients" 
                            :modal_id="'metric-filter-modal'"
                            :client_id="local_client.id" 
                            ref="mf_builder" 
                            :type="local_type" 
                            @update="mfAdded"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'MetricFilterList',
    components: {
        Swal
    },
    props: {
        metrics_filters: {
            type: Array,
            required: true
        },
        data_blends: {
            type: Array,
            required: true
        },
        clients: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            local_mfs: [],
            visible_metrics_filters: [],
            local_type: '',
            local_data_blends: null,
            local_client: {
                id: -1,
                name: "*** Global Calculations & Filters ***",
                url: null
            },
            local_client_selected: -1,
            data_blend: null,
            data_types: ['metric', 'filter', 'dimension'],
            search_key: '',
            get_usage: false,
            formatted_clients: [{ value: -1, text: "*** Global Calculations & Filters ***" }]
        };
    },
    beforeMount() {
        this.local_mfs = this.metrics_filters || [];
        this.local_data_blends = this.data_blends || [];

        // Set default local_client to empty (global)
        this.local_client = {
            id: -1,
            name: "*** Global Calculations & Filters ***",
            url: null
        };

        // Update the formatted clients list
        this.updateFormattedClients();

        // Only set data_blend if local_data_blends has items
        if (this.local_data_blends && this.local_data_blends.length > 0) {
            // Try to find a constituent data blend first (most commonly used)
            const constituentBlend = this.local_data_blends.find(db => db.type === 'constituent');
            if (constituentBlend) {
                this.data_blend = constituentBlend;
            } else {
                // If no constituent blend, use the first one
                this.data_blend = this.local_data_blends[0];
            }

            // Load the metric filter lists
            this.visible_metrics_filters = this.loadMetricFilterLists();
        } else {
            this.visible_metrics_filters = [];
        }
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (let i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    tooltipList[index].hide();
                });
            })(i);
        }
    },
    watch: {
        clients: {
            handler() {
                this.updateFormattedClients();
            },
            deep: true
        }
    },
    methods: {
        loadMetricFilterLists() {
            let arr = [];
            // Check if data_blend is defined before accessing its properties
            if (!this.data_blend) {
                return arr;
            }

            // Check if global is selected (id = -1) or if local_client doesn't have an id property
            const isGlobalSelected = !this.local_client || this.local_client.id === -1;

            for (let i = 0; i < this.local_mfs.length; i++) {
                const mf = this.local_mfs[i];
                
                // Ensure data_blend_id comparison handles both null and number types consistently
                const mfDataBlendId = mf.data_blend_id === null || mf.data_blend_id === undefined ? null : Number(mf.data_blend_id);
                const currentDataBlendId = this.data_blend.id === null || this.data_blend.id === undefined ? null : Number(this.data_blend.id);
                
                //If it is the given type and it is assigned to the selected client (or the template)
                if (mf.data_blend_type === this.data_blend.type &&
                    (mfDataBlendId === null && isGlobalSelected ||
                    (!isGlobalSelected && mfDataBlendId === currentDataBlendId))) {
                    arr.push(mf);
                }
            }

            return arr;
        },
        updateFormattedClients() {
            if (!this.clients || !Array.isArray(this.clients)) {
                this.formatted_clients = [{ value: -1, text: "*** Global Calculations & Filters ***" }];
                return;
            }

            // Start with the global option
            const options = [{ value: -1, text: "*** Global Calculations & Filters ***" }];

            // Add client options
            this.clients.forEach(client => {
                if (client && typeof client === 'object' && client.id !== undefined && client.name) {
                    // Store the original client object in the value
                    options.push({
                        value: client.id,
                        text: client.name,
                        original: client,
                        url: client.url
                    });
                }
            });
            this.formatted_clients = options;
        },
        getUsage() {
            this.get_usage = true;
            window.axios.post('/api/metric_filter/usage').then(response => {
                let mfs = response.data.metric_filters;

                for (let i = 0; i < this.local_mfs.length; i++) {
                    for (let j = 0; j < mfs.length; j++) {
                        if (mfs[j].id === this.local_mfs[i].id) {
                            this.local_mfs[i].page_usage_count = mfs[j].page_usage_count;
                            this.local_mfs[i].chart_usage_count = mfs[j].chart_usage_count;
                            break;
                        }
                    }
                }
                this.$forceUpdate();
            });
        },
        searchByName() {
            let arr = this.loadMetricFilterLists();
            if (this.search_key === '') {
                this.visible_metrics_filters = arr;
            } else { // search against chart name and title for now
                this.visible_metrics_filters = arr.filter(m =>
                    m.admin_name.toLowerCase().includes(this.search_key.toLowerCase())
                );
            }
        },
        mfAdded(mf) {
            //Add to the array and re-sort
            //Look through local_mfs to see if the ID is already in there
            let found = false;
            for (let i = 0; i < this.local_mfs.length; i++) {
                if (this.local_mfs[i].id === mf.id) {
                    this.local_mfs[i] = mf;
                    found = true;
                    break;
                }
            }

            if (!found) {
                this.local_mfs.push(mf);
            }

            //Resort the local_mfs based upon name (case insensitive)
            this.local_mfs.sort((a, b) => a.admin_name.toLowerCase() > b.admin_name.toLowerCase() ? 1 : -1);
            
            // Force update visible list by reloading the filtered list
            this.visible_metrics_filters = this.loadMetricFilterLists();
            
            // Apply search filter if there's a search term
            if (this.search_key !== '') {
                this.searchByName();
            }
            
            this.closeModal();
        },
        getDataTypeOptions(type) {
            let arr = [];
            for (let i = 0; i < this.visible_metrics_filters.length; i++) {
                if (this.visible_metrics_filters[i].type === type) {
                    arr.push(this.visible_metrics_filters[i]);
                }
            }
            return arr;
        },
        closeModal() {
            const myModalEl = document.getElementById('metric-filter-modal');
            const modal = bootstrap.Modal.getInstance(myModalEl);
            modal.hide();
        },
        changeClient() {
            // Handle null/undefined case
            if (!this.local_client_selected) {
                // Reset to global if null
                this.local_client_selected = -1;
            }

            // Get the value - handle both number and object cases
            const selectedValue = typeof this.local_client_selected === 'object' && this.local_client_selected !== null
                ? this.local_client_selected.value
                : this.local_client_selected;

            // Find the selected client from formatted_clients
            const selectedClientOption = this.formatted_clients.find(c => c.value === selectedValue);

            // If we found a client option with the original client object, use that
            if (selectedClientOption && selectedClientOption.original) {
                this.local_client = selectedClientOption.original;
            } else {
                // Fallback to finding the client in the clients array
                this.local_client = this.clients.find(c => c.id === selectedValue);
            }

            // Store the current data blend for later comparison
            const currentDataBlend = this.data_blend;

            // Reset data_blend to null to ensure proper selection logic
            this.data_blend = null;

            if (this.local_client && this.local_client.id !== -1) {
                // Check if the client has a URL property
                if (!this.local_client.url) {
                    console.error("Client URL is missing");
                    this.local_data_blends = [];
                    this.visible_metrics_filters = [];
                    return;
                }

                // Make an API call to get the datablends for the selected client
                window.axios.get(`/api/client/${this.local_client.url}/datablends`)
                    .then(response => {
                        // Update local_data_blends with the response from the API
                        this.local_data_blends = response.data.data || [];

                        // Make sure local_data_blends is valid
                        if (this.local_data_blends && this.local_data_blends.length > 0) {
                            // First try to find the exact same data blend by ID
                            if (currentDataBlend && currentDataBlend.id) {
                                const sameDataBlend = this.local_data_blends.find(db => db.id === currentDataBlend.id);
                                if (sameDataBlend) {
                                    this.data_blend = sameDataBlend;
                                }
                            }

                            // If we couldn't find the same data blend, try to find one with the same type
                            if (!this.data_blend && currentDataBlend && currentDataBlend.type) {
                                const sameTypeDataBlend = this.local_data_blends.find(db => db.type === currentDataBlend.type);
                                if (sameTypeDataBlend) {
                                    this.data_blend = sameTypeDataBlend;
                                }
                            }

                            // If we still don't have a data blend, use the first one
                            if (!this.data_blend) {
                                this.data_blend = this.local_data_blends[0];
                            }

                            // Update the visible metrics filters based on the selected data blend
                            this.visible_metrics_filters = this.loadMetricFilterLists();
                        } else {
                            // If no data blends are available, set visible_metrics_filters to empty array
                            this.visible_metrics_filters = [];
                        }
                    })
                    .catch(error => {
                        console.error("Error fetching datablends:", error);
                        this.local_data_blends = [];
                        this.visible_metrics_filters = [];
                    });
            } else {
                // Global data blends
                this.local_data_blends = this.data_blends;
                // Set local_client to the global option
                this.local_client = {
                    id: -1,
                    name: "*** Global Calculations & Filters ***",
                    url: null
                };

                // Make sure local_data_blends is valid
                if (this.local_data_blends && this.local_data_blends.length > 0) {
                    // First try to find the exact same data blend by ID
                    if (currentDataBlend && currentDataBlend.id) {
                        const sameDataBlend = this.local_data_blends.find(db => db.id === currentDataBlend.id);
                        if (sameDataBlend) {
                            this.data_blend = sameDataBlend;
                        }
                    }

                    // If we couldn't find the same data blend, try to find one with the same type
                    if (!this.data_blend && currentDataBlend && currentDataBlend.type) {
                        const sameTypeDataBlend = this.local_data_blends.find(db => db.type === currentDataBlend.type);
                        if (sameTypeDataBlend) {
                            this.data_blend = sameTypeDataBlend;
                        }
                    }

                    // If we still don't have a data blend, use the first one
                    if (!this.data_blend) {
                        this.data_blend = this.local_data_blends[0];
                    }

                    // Update the visible metrics filters based on the selected data blend
                    this.visible_metrics_filters = this.loadMetricFilterLists();
                } else {
                    // If no data blends are available, set visible_metrics_filters to empty array
                    this.visible_metrics_filters = [];
                }
            }
        },
        changeDataSource() {
            // If data_blend is null, set it to the first available data blend
            if (!this.data_blend && this.local_data_blends && this.local_data_blends.length > 0) {
                this.data_blend = this.local_data_blends[0];
            }

            // Only proceed if data_blend is valid
            if (this.data_blend) {
                // Reset search if any
                this.search_key = '';
                // Update the visible metrics filters based on the selected data blend
                this.visible_metrics_filters = this.loadMetricFilterLists();
            } else {
                // If data_blend is still null, set visible_metrics_filters to empty array
                this.visible_metrics_filters = [];
            }
        },
        addMetricFilter(type) {
            this.local_type = type;
            this.$refs.mf_builder.setMetricFilter(null);
        },
        copyMetricFilter(mf) {
            let new_mf = JSON.parse(JSON.stringify(mf));
            this.local_type = mf.type;
            new_mf.id = null;
            this.$refs.mf_builder.setMetricFilter(new_mf);
        },
        editMetricFilter(mf) {
            let new_mf = JSON.parse(JSON.stringify(mf));
            this.local_type = new_mf.type;
            this.$refs.mf_builder.setMetricFilter(new_mf);
        },
        deleteMetricFilter(el) {
            Swal.fire({
                title: "Are you sure you want to delete this " + el.type + "?",
                text: "When you delete it, all charts and reports that utilize it will also be deleted. (That is a big deal!)",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    window.axios.delete('/api/metric_filter/' + el.id);

                    for (let i = 0; i < this.local_mfs.length; i++) {
                        if (this.local_mfs[i].id === el.id) {
                            this.local_mfs.splice(i, 1);
                            break;
                        }
                    }
                    this.visible_metrics_filters = this.loadMetricFilterLists();
                    this.$forceUpdate();

                    Swal.fire({
                        title: "Deleted",
                        text: "The " + el.type + " has been deleted.",
                        icon: "success",
                        showConfirmButton: false,
                        timer: 1500,
                        customClass: {
                            popup: 'swal-success'
                        },
                    });
                }
            });
        }
    }
});
</script>
